import { useAuth } from '@/components/auth/AuthProvider';
import { apiClient, ApiClientOptions } from '@/utils/apiClient';

interface QueryOptions extends ApiClientOptions {
  failMessage?: string;
}

const useApi = () => {
  const { getAccessTokenSilently, isAuthenticated, recoverFromStaleSession } = useAuth();

  /**
   * Queries an endpoint of the VoteMate backend. Adds auth headers when a valid session exists;
   * falls back to an unauthenticated request when the session is stale or absent (guest endpoints
   * answer fine without a token).
   * @param endpoint String representing the desired endpoint, including query params.
   * @param queryOptions Pass-through of `fetch()` options. Some headers are overridden. Can
   * include a `failMessage` string to specify what to print when requests fail.
   * @returns
   */
  const query = async (endpoint: string, queryOptions?: QueryOptions) => {
    let accessToken: string | undefined;
    if (isAuthenticated) {
      try {
        accessToken = await getAccessTokenSilently();
      } catch (err) {
        // A `localstorage`-cached session can report `isAuthenticated === true`
        // while its refresh token is gone, so the silent fetch throws
        // `Missing Refresh Token` / `invalid_grant`. Treat the session as
        // unrecoverable: clear it (so the app settles into a clean guest
        // state) and continue without a token. Endpoints used by guests
        // (e.g. the national ballot) answer fine unauthenticated, so this
        // degrades gracefully instead of surfacing "Error Loading Ballot".
        console.warn('Silent token fetch failed; continuing as guest:', err);
        void recoverFromStaleSession();
      }
    }
    return apiClient(endpoint, { ...queryOptions, accessToken });
  };

  return query;
};

export default useApi;
